import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import dashboardService from '../../../api';
import WidgetHeader from './WidgetHeader';
import WidgetContent from './WidgetContent';
import WidgetEditor from './WidgetEditor';
import WidgetSummary from './WidgetSummary';
import SqlQueryModal from './SqlQueryModal';
import { formatText } from '../../../../../utils/formatters';
import { buildApiUrl } from '../../../../../services/apiConfig';
import { Info } from 'lucide-react';

const Widget = ({
  id,
  title,
  subtitle,
  status,
  category,
  prompt: initialPrompt = '',
  chartPrompt: initialChartPrompt = null,
  customerId,
  tool,
  onContentChange,
  useCaseId,
  context,
  onDelete,
  hasAvatar = false,
  ...props
}) => {
  // State variables
  const [flipped, setFlipped] = useState(false);
  const [editing, setEditing] = useState(false);
  const [prompt, setPrompt] = useState(initialPrompt);
  const [chartPrompt, setChartPrompt] = useState(initialChartPrompt);
  const [widgetTitle, setWidgetTitle] = useState(title);
  const [tempPrompt, setTempPrompt] = useState(initialPrompt);
  const [tempTitle, setTempTitle] = useState(title);
  const [tempChartPrompt, setTempChartPrompt] = useState(initialChartPrompt);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [textResult, setTextResult] = useState('');
  const [chartHTML, setChartHTML] = useState(null);
  const [isHovering, setIsHovering] = useState(false);
  const [isTableChart, setIsTableChart] = useState(false);
  const [rawSqlResponse, setRawSqlResponse] = useState('');
  const [showSummary, setShowSummary] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryText, setSummaryText] = useState('');
  const [summaryError, setSummaryError] = useState(null);
  const [dynamicCategory, setDynamicCategory] = useState(category);
  const [storedUserId, setStoredUserId] = useState();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [chartSummary, setChartSummary] = useState('');
  const [sqlQuery, setSqlQuery] = useState('');
  const [showSqlQuery, setShowSqlQuery] = useState(false);

  // Refs
  const contentFetchedRef = useRef(false);
  const chartFetchedRef = useRef(false);
  const lastPromptRef = useRef(initialPrompt);
  const lastChartPromptRef = useRef(initialChartPrompt);
  const lastCustomerIdRef = useRef(customerId);
  const prevCustomerIdRef = useRef(customerId);
  const prevContextRef = useRef(context);
  const audioRef = useRef(new Audio());
  const audioTimerRef = useRef(null);

  // API endpoints
  const EMBED_API = buildApiUrl('CHART', '/api/embed-new');
  const SQL_API = buildApiUrl('SQL_AGENT', '/sql-ask');
  const RAG_API = buildApiUrl('RAG', '/rag_base');
  const RETAIL_API = buildApiUrl('RETAIL', '/query');
  const UPLOAD_RAG_API = buildApiUrl('UPLOAD', '/upload-documents');
  const JAMBA_API = buildApiUrl('JAMBA', '/custom-analyze');
  const NOVA_API = buildApiUrl('NOVA', '/Nova_chat_stream');

  // Get user ID from session storage
  useEffect(() => {
    const userId = sessionStorage.getItem('user_id');
    setStoredUserId(userId || 'U1757535255');
  }, []);

  // Determine if the chart is a table
  useEffect(() => {
    if (chartPrompt) {
      setIsTableChart(chartPrompt.toLowerCase().includes('table'));
    } else {
      setIsTableChart(false);
    }
  }, [chartPrompt]);

  // Check if we need to refetch data
  const shouldRefetch = useCallback(() => {
    const promptChanged = lastPromptRef.current !== prompt;
    const chartPromptChanged = lastChartPromptRef.current !== chartPrompt;
    const customerIdChanged = lastCustomerIdRef.current !== customerId;

    lastPromptRef.current = prompt;
    lastChartPromptRef.current = chartPrompt;
    lastCustomerIdRef.current = customerId;

    return promptChanged || chartPromptChanged || customerIdChanged;
  }, [prompt, chartPrompt, customerId]);

  // Update prompt when initialPrompt changes
  useEffect(() => {
    setPrompt(initialPrompt);
    setTempPrompt(initialPrompt);
  }, [initialPrompt]);

  // Update chartPrompt when initialChartPrompt changes
  useEffect(() => {
    setChartPrompt(initialChartPrompt);
    setTempChartPrompt(initialChartPrompt);
  }, [initialChartPrompt]);

  // Clean up audio resources
  useEffect(() => {
    return () => {
      if (audioTimerRef.current) {
        clearTimeout(audioTimerRef.current);
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, []);

  // Get the final prompt with customer ID if needed
  const getFinalPrompt = useCallback(() => {
    let fp = prompt;
    if (customerId) {
      fp = `${prompt} for person id ${customerId}`;
    }
    if (chartPrompt) {
      fp = fp + ' in json';
    }
    return fp;
  }, [prompt, customerId, chartPrompt]);

  // Handle widget deletion
  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this widget?')) {
      if (typeof onDelete === 'function') {
        await onDelete(id);
      }
    }
  };

  // Handle widget editing
  const handleEdit = () => {
    setTempPrompt(prompt);
    setTempChartPrompt(chartPrompt);
    setTempTitle(title);
    setEditing(true);
  };

  const handleInfoClick = () => {
    setShowSqlQuery(true);
  };

  // Handle saving widget changes
  const handleSave = async () => {
    const newPrompt = tempPrompt;
    const newChartPrompt = tempChartPrompt && tempChartPrompt.trim() !== '' ? tempChartPrompt.trim() : null;
    const newTitle = tempTitle.trim();

    setPrompt(newPrompt);
    setChartPrompt(newChartPrompt);
    setWidgetTitle(newTitle);
    setEditing(false);

    if (newPrompt !== prompt || newChartPrompt !== chartPrompt) {
      contentFetchedRef.current = false;
      chartFetchedRef.current = false;
    }

    try {
      // Use the API service to update the widget
      await dashboardService.updateWidget(id, {
        tool_prompt: newPrompt,
        visual_prompt: newChartPrompt,
        name: newTitle
      });
    } catch (err) {
      console.error("❌ Error updating widget in DB:", err);
    }
  };

  // Handle canceling edit mode
  const handleCancel = () => setEditing(false);

  // Handle avatar play button click
  const handleAvatarPlay = async () => {
    if (isProcessing) return;
    setIsProcessing(true);

    try {
      const ttsResponse = await fetch(buildApiUrl('TTS', '/tts_normalize'), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: textResult || prompt,
          "summ_flag": true
        }),
      });

      if (!ttsResponse.ok) throw new Error("TTS API failed: " + await ttsResponse.text());

      // Get the audio blob
      const wavBlob = await ttsResponse.blob();

      // Create form data for processing
      const formData = new FormData();
      formData.append("file", wavBlob, "audio.wav");

      // Process the audio
      const audioProcessing = await fetch(buildApiUrl('AUDIO', '/process_audio'), {
        method: "POST",
        body: formData,
      });

      if (!audioProcessing.ok) throw new Error("Audio processing failed: " + await audioProcessing.text());

      // Get the processed audio URL and duration
      const responseData = await audioProcessing.json();
      const audioUrl = responseData.audio_url;
      const audioLength = responseData.audio_length || 0;

      // Set playing state immediately to hide the button
      setIsPlaying(true);

      // Create a timer to track audio playback duration
      if (audioTimerRef.current) {
        clearTimeout(audioTimerRef.current);
      }

      // Set a timer to reset isPlaying after audio_length seconds
      if (audioLength > 0) {
        audioTimerRef.current = setTimeout(() => {
          setIsPlaying(false);
        }, audioLength * 1000); // Convert seconds to milliseconds
      }

      // Play the audio
      if (audioRef.current) {
        if (!audioRef.current.play) {
          audioRef.current = new Audio(audioUrl);
        } else {
          audioRef.current.src = audioUrl;
        }

        audioRef.current.oncanplaythrough = () => {
          audioRef.current.play()
            .then(() => { })
            .catch(err => {
              setIsPlaying(false);
              if (audioTimerRef.current) {
                clearTimeout(audioTimerRef.current);
              }
            });
        };

        audioRef.current.onerror = () => {
          setIsPlaying(false);
          if (audioTimerRef.current) {
            clearTimeout(audioTimerRef.current);
          }
        };

        audioRef.current.onended = () => {
          setIsPlaying(false);
          if (audioTimerRef.current) {
            clearTimeout(audioTimerRef.current);
          }
        };

        // Load the audio
        audioRef.current.load();
      } else {
        throw new Error("Audio reference not available");
      }
    } catch (err) {
      console.error("Audio playback error:", err);
      setIsPlaying(false);
    } finally {
      setIsProcessing(false);
    }
  };

  // Fetch content for the widget
  const fetchContent = useCallback(async () => {
    if (contentFetchedRef.current && !shouldRefetch()) return;
    if (chartPrompt !== null && chartFetchedRef.current && !shouldRefetch()) return;

    setLoading(true);
    setError(null);

    try {
      if (tool === 'SQL Query Assistant') {
        let finalPrompt = getFinalPrompt();
        const formData = new FormData();
        formData.append('question', finalPrompt);
        formData.append('cust_id', customerId);
        formData.append('advisor_id', storedUserId);
        formData.append('use_case_id', useCaseId);

        const sqlRes = await axios.post(SQL_API, formData);
        const data = sqlRes.data;
        const answer = data?.response?.answer || '';
        const formattedResult = formatText(answer, finalPrompt);

        const sumFormData = new FormData();
        const KeyInsightsPrompt = prompt + 'Based on the data provided, give me ONLY 3-4 key insights or patterns in bullet points. ' +
          'include current tech or market news insights in bullet points form. ' +
          'Do NOT repeat the raw data values. Focus ONLY on meaningful trends, patterns, or notable observations. ' +
          'Keep each insight to max 12 words. Start directly with the bullet points.';

        sumFormData.append('question', KeyInsightsPrompt);
        sumFormData.append('cust_id', customerId);
        sumFormData.append('advisor_id', storedUserId);
        sumFormData.append('use_case_id', useCaseId);

        const sumsqlRes = await axios.post(SQL_API, sumFormData);
        const sumData = sumsqlRes.data;
        if (sumData?.response?.query) {
          setSqlQuery(data.response.query);
        }
        setRawSqlResponse(formatText(sumData?.response?.answer, KeyInsightsPrompt));

        if (chartPrompt && !chartFetchedRef.current) {
          let rawAnswer = answer;
          const cleaned = rawAnswer.replace(/```json|```/g, '');
          let parsed;

          try {
            parsed = JSON.parse(cleaned);
          } catch (e) {
            console.error("Failed to parse JSON response:", e);
            setLoading(false);
            return;
          }

          let parsedData;
          if (Array.isArray(parsed)) {
            parsedData = parsed;
          } else if (
            typeof parsed === 'object' &&
            Object.values(parsed).length === 1 &&
            Array.isArray(Object.values(parsed)[0])
          ) {
            parsedData = Object.values(parsed)[0];
          } else {
            parsedData = [parsed];
          }

          if (typeof onContentChange === 'function' && id !== undefined) {
            onContentChange(id, JSON.stringify(parsedData));
          }

          const cssColor = getComputedStyle(document.documentElement).getPropertyValue('--color-primary').trim();
          const finalColor = cssColor.startsWith('#') ? cssColor : `#${cssColor}`;

          const embedRes = await fetch(EMBED_API, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              input: JSON.stringify(parsedData),
              query: chartPrompt,
              primary_color: finalColor,
            }),
          });

          let html = await embedRes.text();
          html = applyChartStyles(html);

          const blob = new Blob([html], { type: 'text/html' });
          setChartHTML(URL.createObjectURL(blob));

          try {
            const summaryPrompt = "Provide a concise analysis of this data in 2-3 bullet points.";
            const summaryFormData = new FormData();
            summaryFormData.append('question', `${summaryPrompt} ${JSON.stringify(parsedData)}`);
            summaryFormData.append('cust_id', customerId);
            summaryFormData.append('advisor_id', storedUserId);
            summaryFormData.append('use_case_id', useCaseId);

            const summaryRes = await axios.post(SQL_API, summaryFormData);
            const summaryData = summaryRes.data;
            const summaryText = summaryData?.response?.answer || '';
            setChartSummary(formatText(summaryText, summaryPrompt));
          } catch (e) {
            console.error("Failed to generate chart summary:", e);
            setChartSummary("Failed to generate chart summary.");
          }
          chartFetchedRef.current = true;
        } else {
          setTextResult(formattedResult);
          if (typeof onContentChange === 'function' && id !== undefined) {
            onContentChange(id, formattedResult);
          }
        }
      } else if (tool === 'Transcript Analysis') {
        if (!context) {
          const noContextMsg = 'No context provided for analysis.';
          setTextResult(noContextMsg);
          if (typeof onContentChange === 'function' && id !== undefined) {
            onContentChange(id, noContextMsg);
          }
          contentFetchedRef.current = true;
          setLoading(false);
          return;
        }

        const transcriptionFile = new Blob([context], { type: 'text/plain' });
        const formData = new FormData();
        formData.append('file', transcriptionFile, 'transcription.txt');
        formData.append('prompt', prompt);

        const response = await axios.post(JAMBA_API, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (response.data) {
          const formattedText = formatText(response.data.data, prompt);
          setTextResult(formattedText);
          if (typeof onContentChange === 'function' && id !== undefined) {
            onContentChange(id, formattedText);
          }
        }
      } else if (tool === 'Retrieval Tool') {
        const response = await axios.post(RAG_API, { query: prompt });

        if (response.data && response.data.response) {
          const formattedText = formatText(response.data.response, prompt);
          setTextResult(formattedText);
          if (typeof onContentChange === 'function' && id !== undefined) {
            onContentChange(id, formattedText);
          }
        }
      } else if (tool === 'Retail Tool') {
        const response = await axios.post(RETAIL_API, { query: prompt });

        if (response.data && response.data.response) {
          const formattedText = formatText(response.data.response, prompt);
          setTextResult(formattedText);
          if (typeof onContentChange === 'function' && id !== undefined) {
            onContentChange(id, formattedText);
          }
        }
      } else if (tool === 'Transcript Insight Engine') {
        const finalPrompt = getFinalPrompt();

        if (!context) {
          const noContextMsg = 'No context provided for analysis.';
          setTextResult(noContextMsg);
          if (typeof onContentChange === 'function' && id !== undefined) {
            onContentChange(id, noContextMsg);
          }
          contentFetchedRef.current = true;
          setLoading(false);
          return;
        }

        const formData = new FormData();
        formData.append('query', `having this conversation as context: ${context} ; ${finalPrompt}`);

        const response = await axios.post(RAG_API, formData);

        if (response.data && response.data.response) {
          const formattedText = formatText(response.data.response, prompt);
          setTextResult(formattedText);
          if (typeof onContentChange === 'function' && id !== undefined) {
            onContentChange(id, formattedText);
          }
        }
      } else if (tool === 'NOVA') {
        try {
          const formData = new FormData();
          formData.append("query", getFinalPrompt());
          formData.append("cust_id", customerId);
          formData.append("advisor_id", storedUserId);
          formData.append("use_case_id", useCaseId);

          const response = await fetch(NOVA_API, {
            method: "POST",
            body: formData,
          });

          if (!response.ok) {
            throw new Error(`Server responded with ${response.status}`);
          }

          let accumulatedText = "";

          try {
            const { createParser } = await import('eventsource-parser');
            const parser = createParser({
              onEvent: (event) => {
                const type = event.event;
                if (type === "filler" || type === "interrupt" || type === "final") {
                  try {
                    const parsedData = JSON.parse(event.data);
                    if (parsedData.ERROR && typeof parsedData.ERROR === 'string') {
                      throw new Error(parsedData.ERROR);
                    }
                    if (parsedData.data.response) {
                      accumulatedText += parsedData.data.response;
                    } else if (parsedData.data) {
                      accumulatedText += parsedData.data;
                    }
                  } catch (error) {
                    console.error("Error processing event data:", error);
                  }
                }
              }
            });

            const reader = response.body.getReader();
            const decoder = new TextDecoder();

            while (true) {
              const { value, done } = await reader.read();
              if (done) break;
              const chunk = decoder.decode(value, { stream: true });
              parser.feed(chunk);
            }

            if (chartPrompt && !chartFetchedRef.current) {
              try {
                let jsonData;

                try {
                  const parsedResponse = JSON.parse(accumulatedText.trim());
                  if (parsedResponse && parsedResponse.data && Array.isArray(parsedResponse.data)) {
                    jsonData = parsedResponse.data;
                  } else {
                    jsonData = parsedResponse;
                  }
                } catch (e) {
                  const jsonRegex = /```(?:json)?\s*([\s\S]*?)```/;
                  const match = accumulatedText.match(jsonRegex);

                  if (match && match[1]) {
                    try {
                      const parsedBlock = JSON.parse(match[1].trim());
                      if (parsedBlock && parsedBlock.data && Array.isArray(parsedBlock.data)) {
                        jsonData = parsedBlock.data;
                      } else {
                        jsonData = parsedBlock;
                      }
                    } catch (blockError) {
                      throw new Error("Failed to parse JSON from code block");
                    }
                  } else {
                    // If no valid JSON found, create a structured format from text lines
                    const lines = accumulatedText.split('\n').filter(line => line.trim());
                    jsonData = lines.map((line, index) => ({
                      id: index + 1,
                      content: line.trim()
                    }));
                  }
                }

                if (typeof onContentChange === 'function' && id !== undefined) {
                  onContentChange(id, JSON.stringify(jsonData));
                }

                const cssColor = getComputedStyle(document.documentElement).getPropertyValue('--color-primary').trim();
                const finalColor = cssColor.startsWith('#') ? cssColor : `#${cssColor}`;

                // Generate chart
                const embedRes = await fetch(EMBED_API, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    input: JSON.stringify(jsonData),
                    query: chartPrompt,
                    primary_color: finalColor,
                  }),
                });

                let html = await embedRes.text();
                html = applyChartStyles(html);

                const blob = new Blob([html], { type: 'text/html' });
                setChartHTML(URL.createObjectURL(blob));

                // Generate chart summary
                try {
                  const summaryPrompt = "Provide a concise analysis of this data in 2-3 bullet points.";
                  const summaryFormData = new FormData();
                  summaryFormData.append('query', `${summaryPrompt} ${JSON.stringify(jsonData)}`);
                  summaryFormData.append('cust_id', customerId);
                  summaryFormData.append('advisor_id', storedUserId);
                  summaryFormData.append('use_case_id', useCaseId);

                  const summaryRes = await fetch(NOVA_API, {
                    method: "POST",
                    body: summaryFormData,
                  });

                  if (!summaryRes.ok) {
                    throw new Error("Failed to generate chart summary");
                  }

                  let summaryText = "";
                  const { createParser: createSummaryParser } = await import('eventsource-parser');
                  const summaryParser = createSummaryParser({
                    onEvent: (event) => {
                      const type = event.event;
                      if (type === "filler" || type === "interrupt" || type === "final") {
                        try {
                          const parsedData = JSON.parse(event.data);
                          if (parsedData.data.response) {
                            summaryText += parsedData.data.response;
                          } else if (parsedData.data) {
                            summaryText += parsedData.data;
                          }
                        } catch (error) {
                          console.error("Error processing summary event data:", error);
                        }
                      }
                    }
                  });

                  const summaryReader = summaryRes.body.getReader();
                  while (true) {
                    const { value, done } = await summaryReader.read();
                    if (done) break;
                    const chunk = decoder.decode(value, { stream: true });
                    summaryParser.feed(chunk);
                  }

                  setChartSummary(formatText(summaryText, summaryPrompt));
                } catch (e) {
                  console.error("Failed to generate chart summary:", e);
                  setChartSummary("Failed to generate chart summary.");
                }

                chartFetchedRef.current = true;
              } catch (chartError) {
                console.error("Error generating chart from NOVA response:", chartError);
                setError("Failed to generate chart visualization. Please try again.");
                chartFetchedRef.current = false;
              }
            } else {
              // For non-chart responses, just format the text
              const formattedText = formatText(accumulatedText, prompt);
              setTextResult(formattedText);
              if (typeof onContentChange === 'function' && id !== undefined) {
                onContentChange(id, formattedText);
              }
            }
          } catch (streamError) {
            console.error("Stream processing error:", streamError);
            throw streamError;
          }
        } catch (e) {
          console.error("Error fetching NOVA content:", e);
          setError("Failed to load NOVA content. Please try again.");
        }
      }

      contentFetchedRef.current = true;
    } catch (e) {
      if (chartPrompt !== null) {
        chartFetchedRef.current = false;
      }
      console.error("Error fetching content:", e);
      setError("Failed to load content. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [tool, prompt, chartPrompt, customerId, id, onContentChange, getFinalPrompt, context, storedUserId, useCaseId, shouldRefetch]);

  // Helper function to apply chart styles
  const applyChartStyles = (html) => {
    html = html.replace(
      /(<head[^>]*>)/i,
      `$1 <meta name="viewport" content="width=device-width, initial-scale=1.0"> <style>
        * { box-sizing: border-box; }
        html { margin: 0 !important; padding: 0 !important; width: 100% !important; height: 100% !important; background: white !important; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important; overflow-x: hidden !important; }
        body { margin: 0 !important; padding: 15px !important; width: 100% !important; min-height: 100% !important; height: 100% !important; background: white !important; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important; overflow: visible !important; }
        ::-webkit-scrollbar { width: 8px !important; height: 8px !important; }
        ::-webkit-scrollbar-track { background: #f1f1f1 !important; }
        ::-webkit-scrollbar-thumb { background: #888 !important; border-radius: 4px !important; }
        ::-webkit-scrollbar-thumb:hover { background: #555 !important; }
        .plotly-graph-div, .js-plotly-plot, #plotly-div, [id*="plotly"] { width: 100% !important; height: 100% !important; min-height: 420px !important; }
        .main-svg { width: 100% !important; height: 100% !important; }
        h1, h2, h3, h4 { color: var(--color-primary) !important; font-weight: 700 !important; margin: 0 0 16px 0 !important; font-size: 18px !important; text-align: center !important; }
        .table-wrap { width: 100% !important; max-height: 100% !important; overflow-y: auto !important; overflow-x: auto !important; }
        table { width: 100% !important; border-collapse: separate !important; border-spacing: 0 !important; background: white !important; border-radius: 12px !important; overflow: hidden !important; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12) !important; margin: 10px 0 !important; }
        thead th { position: sticky !important; top: 0 !important; z-index: 1 !important; }
        thead { background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 50%, var(--color-primary-darker, #0A4A94) 100%) !important; box-shadow: 0 3px 6px rgba(var(--color-primary-rgb, 21, 117, 219), 0.3) !important; }
        thead th { color: white !important; font-weight: 700 !important; padding: 16px 20px !important; text-align: left !important; font-size: 13px !important; text-transform: uppercase !important; letter-spacing: 1px !important; border: none !important; white-space: nowrap !important; text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2) !important; }
        tbody tr { transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important; border-bottom: 1px solid #e2e8f0 !important; }
        tbody tr:nth-child(odd) { background: #ffffff !important; }
        tbody tr:nth-child(even) { background: #f8fafc !important; }
        tbody tr:hover { background: linear-gradient(90deg, var(--color-primary-light) 0%, var(--color-primary-lighter) 100%) !important; transform: translateX(4px) !important; box-shadow: -4px 0 0 0 var(--color-primary), 0 2px 8px rgba(var(--color-primary-rgb, 21, 117, 219), 0.2) !important; }
        tbody td { padding: 14px 20px !important; font-size: 14px !important; font-weight: 500 !important; border: none !important; }
        tbody td:first-child { color: var(--color-primary) !important; font-weight: 700 !important; font-size: 15px !important; }
        tbody td:last-child { text-align: right !important; font-variant-numeric: tabular-nums !important; }
        tbody tr:last-child { border-bottom: none !important; }
        .legend { transform: translateY(20px) !important; }
        .scatterlayer path { stroke-width: 3px !important; filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1)) !important; }
        .barlayer rect { filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.08)) !important; }
        .pielayer { filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15)) !important; }
        .slice path { stroke: white !important; stroke-width: 3px !important; }
        .hoverlayer { font-family: 'Inter', sans-serif !important; }
        .hovertext { font-size: 13px !important; font-weight: 600 !important; padding: 8px 12px !important; border-radius: 6px !important; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important; }
        .gridlayer path { stroke: #e2e8f0 !important; stroke-width: 1px !important; }
        .xtick text, .ytick text { font-size: 12px !important; font-weight: 600 !important; fill: #475569 !important; }
        .xtick line, .ytick line { stroke: #cbd5e1 !important; }
        .gtitle { font-size: 14px !important; font-weight: 700 !important; fill: #1e293b !important; }
        .plotly-notifier, .modebar-btn[data-title="Produced with Plotly"] { display: none !important; }
        @media (max-width: 768px) {
          body { padding: 10px !important; }
          .plotly-graph-div { height: 400px !important; }
          table { font-size: 12px !important; }
          thead th { padding: 10px 12px !important; font-size: 11px !important; }
          tbody td { padding: 8px 12px !important; font-size: 12px !important; }
        }
      </style>`
    );

    html = html.replace(
      /(<\/body>)/i,
      `<script>
        (function () {
          function wrapTables() {
            const tables = document.querySelectorAll('table');
            tables.forEach(function(tbl){
              if (tbl.parentElement && tbl.parentElement.classList.contains('table-wrap')) return;
              var wrap = document.createElement('div');
              wrap.className = 'table-wrap';
              tbl.parentNode.insertBefore(wrap, tbl);
              wrap.appendChild(tbl);
            });
            var contentHeight = document.body.scrollHeight;
            try {
              parent.postMessage({ type: 'EMBED_HEIGHT', h: contentHeight }, '*');
            } catch(e){
              console.error('[Iframe Content] Error posting message:', e);
            }
          }
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', wrapTables);
          } else {
            wrapTables();
          }
          window.addEventListener('load', function() {
            setTimeout(wrapTables, 100);
          });
        })();
      </script> $1`
    );

    return html;
  };

  const generateSummary = async () => {
    // Toggle summary visibility if it already exists
    if (summaryText && showSummary) {
      setShowSummary(false);
      return;
    } else if (summaryText) {
      setShowSummary(true);
      return;
    }

    // Start loading state
    setSummaryLoading(true);
    setSummaryError(null);
    setShowSummary(true);

    try {
      // Determine which content to use for summary
      let content = '';

      if (tool === 'SQL Query Assistant' && rawSqlResponse) {
        content = rawSqlResponse;
      } else if (chartHTML && chartSummary) {
        content = chartSummary;
      } else if (textResult) {
        content = textResult;
      } else if (context) {
        content = context;
      } else {
        throw new Error("No content available to summarize");
      }

      // Create a prompt for generating the summary
      const summaryPrompt = "Suggest the next strategic step in one sentence based on this data given this content:" + content;

      // Handle different tools with their specific API requirements
      if (tool === 'NOVA') {
        const formData = new FormData();
        formData.append("query", summaryPrompt);
        formData.append("cust_id", customerId);
        formData.append("advisor_id", storedUserId);
        formData.append("use_case_id", useCaseId);

        const response = await fetch(NOVA_API, {
          method: "POST",
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`Server responded with ${response.status}`);
        }

        let accumulatedText = "";

        // Process streaming response from NOVA
        const { createParser } = await import('eventsource-parser');
        const parser = createParser({
          onEvent: (event) => {
            const type = event.event;
            if (type === "filler" || type === "interrupt" || type === "final") {
              try {
                const parsedData = JSON.parse(event.data);
                if (parsedData.ERROR && typeof parsedData.ERROR === 'string') {
                  throw new Error(parsedData.ERROR);
                }
                if (parsedData.data.response) {
                  accumulatedText += parsedData.data.response;
                } else if (parsedData.data) {
                  accumulatedText += parsedData.data;
                }
              } catch (error) {
                console.error("Error processing event data:", error);
              }
            }
          }
        });

        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value, { stream: true });
          parser.feed(chunk);
        }

        setSummaryText(formatText(accumulatedText, summaryPrompt));
      }
      else if (tool === 'SQL Query Assistant') {
        const formData = new FormData();
        formData.append('question', summaryPrompt);
        formData.append('cust_id', customerId);
        formData.append('advisor_id', storedUserId);
        formData.append('use_case_id', useCaseId);

        const response = await axios.post(SQL_API, formData);

        if (response.data?.response?.answer) {
          setSummaryText(formatText(response.data.response.answer, summaryPrompt));
        } else {
          throw new Error("Invalid response format from SQL API");
        }
      }
      else if (tool === 'Transcript Analysis' || tool === 'Transcript Insight Engine') {
        const transcriptionFile = new Blob([content], { type: 'text/plain' });
        const formData = new FormData();
        formData.append('file', transcriptionFile, 'transcription.txt');
        formData.append('prompt', summaryPrompt);

        const response = await axios.post(JAMBA_API, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (response.data && response.data.data) {
          setSummaryText(formatText(response.data.data, summaryPrompt));
        } else {
          throw new Error("Invalid response format from Transcript API");
        }
      }
      else if (tool === 'Retrieval Tool') {
        const response = await axios.post(RAG_API, { query: summaryPrompt });

        if (response.data && response.data.response) {
          setSummaryText(formatText(response.data.response, summaryPrompt));
        } else {
          throw new Error("Invalid response format from Retrieval API");
        }
      }
      else if (tool === 'Retail Tool') {
        const response = await axios.post(RETAIL_API, { query: summaryPrompt });

        if (response.data && response.data.response) {
          setSummaryText(formatText(response.data.response, summaryPrompt));
        } else {
          throw new Error("Invalid response format from Retail API");
        }
      }
      else {
        // Default fallback for any other tool
        const formData = new FormData();
        formData.append('question', summaryPrompt);
        formData.append('cust_id', customerId);
        formData.append('advisor_id', storedUserId);
        formData.append('use_case_id', useCaseId);

        const response = await axios.post(SQL_API, formData);

        if (response.data?.response?.answer) {
          setSummaryText(formatText(response.data.response.answer, summaryPrompt));
        } else {
          throw new Error("Invalid response format from API");
        }
      }
    } catch (error) {
      console.error("Error generating summary:", error);
      setSummaryError("Failed to generate summary. Please try again.");
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    if (customerId && customerId !== prevCustomerIdRef.current) {
      contentFetchedRef.current = false;
      chartFetchedRef.current = false;
      prevCustomerIdRef.current = customerId;
    }

    if ((tool === 'Transcript Analysis' || tool === 'Transcript Insight Engine') && context !== prevContextRef.current) {
      contentFetchedRef.current = false;
      chartFetchedRef.current = false;
      prevContextRef.current = context;
    }

    // Check if we need to fetch content
    const shouldFetchContent = () => {
      const isTranscriptTool = tool === 'Transcript Analysis' || tool === 'Transcript Insight Engine';
      return (
        !contentFetchedRef.current && (
          (isTranscriptTool && context) ||
          (tool === 'SQL Query Assistant' && prompt && customerId) ||
          (tool === 'Retrieval Tool' && prompt) ||
          (tool === 'Retail Tool' && prompt) ||
          (tool === 'NOVA' && prompt && customerId)
        )
      );
    };

    // Use a short timeout to ensure all props are properly initialized
    const fetchTimer = setTimeout(() => {
      if (shouldFetchContent()) {
        fetchContent();
      }
    }, 100);

    return () => clearTimeout(fetchTimer);
  }, [tool, prompt, chartPrompt, customerId, context, fetchContent]);

  // Update category based on title
  useEffect(() => {
    let newCategory = category;

    if (title) {
      const titleLower = title.toLowerCase();

      if (titleLower.includes('top risks') || titleLower.includes('insights') ||
        titleLower.includes('personal milestone') || titleLower.includes('reminder')) {
        newCategory = 'immediate attention';
      } else if (titleLower.includes('portfolio')) {
        newCategory = 'portfolio review';
      } else if (titleLower.includes('ai meeting agenda') || titleLower.includes('upcoming goal')) {
        newCategory = 'call preparation';
      }

      setDynamicCategory(newCategory);


    }
  }, [title, id, category]);

  return (
    <div
      className={`bg-white rounded-xl p-5 w-full h-fit flex flex-col transition-all duration-300 origin-center shadow-md mb-6 ${flipped ? 'rotate-y-180' : ''}`}
      style={{ willChange: 'transform, box-shadow' }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <WidgetHeader
        title={widgetTitle}
        subtitle={subtitle}
        status={status}
        category={dynamicCategory}
        isEditing={editing}
        isHovering={isHovering}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onSave={handleSave}
        onCancel={handleCancel}
        tempTitle={tempTitle}
        setTempTitle={setTempTitle}
        showInfoButton={tool === 'SQL Query Assistant' && sqlQuery && !editing}
        onInfoClick={handleInfoClick}
      />
      <SqlQueryModal
        isOpen={showSqlQuery}
        onClose={() => setShowSqlQuery(false)}
        query={sqlQuery}
      />
      {editing ? (
        <WidgetEditor
          tempPrompt={tempPrompt}
          setTempPrompt={setTempPrompt}
          tempChartPrompt={tempChartPrompt}
          setTempChartPrompt={setTempChartPrompt}
        />
      ) : (
        <WidgetContent
          loading={loading}
          error={error}
          chartPrompt={chartPrompt}
          chartHTML={chartHTML}
          isTableChart={isTableChart}
          rawSqlResponse={rawSqlResponse}
          hasAvatar={hasAvatar}
          textResult={textResult}
          prompt={prompt}
          onAvatarPlay={handleAvatarPlay}
          isPlaying={isPlaying}
          isProcessing={isProcessing}
          setIsPlaying={setIsPlaying}
        />
      )}

      <WidgetSummary
        showSummary={showSummary}
        summaryLoading={summaryLoading}
        summaryError={summaryError}
        summaryText={summaryText}
        generateSummary={generateSummary}
      />

      <style jsx>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .animate-shimmer {
          animation: shimmer 1.5s infinite linear;
        }
        [data-scrollable-content], .scrollable-content {
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: #b0b0b0 transparent;
          max-height: 150px;
        }
        [data-scrollable-content]::-webkit-scrollbar, .scrollable-content::-webkit-scrollbar {
          width: 4px;
          background: transparent;
        }
        [data-scrollable-content]::-webkit-scrollbar-thumb, .scrollable-content::-webkit-scrollbar-thumb {
          background: #b0b0b0;
          border-radius: 2px;
        }
        [data-scrollable-content]::-webkit-scrollbar-thumb:hover, .scrollable-content::-webkit-scrollbar-thumb:hover {
          background: #888;
        }
      `}</style>
    </div>

  );
};

export default Widget;